import React from 'react';

// Components
import { Tooltip } from '@/views/components/tooltips';
import IconButton from '@/views/components/buttons/IconButton';
import IntegerValue from '@/views/components/common/IntegerValue';
import ShelfStatus from '@/views/components/shelf-management/ShelfStatus';

// Enums
import { SearchType } from '@/enums/AdvancedSearch';

// Libraries
import classNames from 'classnames';

// Types
import { AdvancedSearchItemForDataTable } from '@/types/advanced-search-item';
import { Cell } from '@tanstack/react-table';
import Fund from '@/types/fund';
import Locale from '@/types/locale';
import Nullable from '@/types/nullable';
import ShelvableInstrument from '@/types/shelvable-instrument';
import User from '@/types/user';
import { ColumnOverride } from '@/types/data-table';

// Utils
import { formatLocalizedString, formatMessage, formatNumber, LOCALE } from '@/utils/i18n';
import { getRiskMetricVal } from '@/utils/advanced-search';
import { getShelfNameFromShelvableInstrument } from '@/utils/user-shelf';

type Props<T extends AdvancedSearchItemForDataTable> = {
  hideSaveItem?: boolean;
  itemState?: any;
  onAddItem?: (item: T) => void;
  onToggleSaved: (item: T) => void;
  showInlineAdd?: boolean;
  showTicker?: boolean;
  type?: SearchType;
  allowedColumns: SearchResultColumns[];
  user?: Nullable<User>;
  locale?: Nullable<Locale>;
  columnOverrides?: Partial<Record<SearchResultColumns, ColumnOverride>>;
};

export enum SearchResultColumns {
  INLINE_ADD = 'inlineAdd',
  TICKER = 'symbol',
  NAME = 'name',
  UNIVERSE = 'universe',
  CLASSIFICATION = 'classification',
  YT = 'KeyMetrics.percentGrowth.YT',
  ONE_YEAR = 'KeyMetrics.percentGrowth.1Y',
  THREE_YEARS = 'KeyMetrics.percentGrowth.3Y',
  FIVE_YEARS = 'KeyMetrics.percentGrowth.5Y',
  TEN_YEARS = 'KeyMetrics.percentGrowth.10Y',
  INC = 'KeyMetrics.percentGrowth.INC',
  MER = 'KeyMetrics.mer',
  BETA = 'KeyMetrics.performance.beta',
  STD_DEV = 'KeyMetrics.performance.standardDeviation',
  IS_SAVED = 'isSaved',
  REMOVE_FROM_FAVORITES = 'removeFromFavorites',
  IS_FOCUSED = 'isFocused',
}

export const getColumnsDefinition = <T extends AdvancedSearchItemForDataTable>(props: Props<T>) => {
  const i18nBasePath = props.type === SearchType.BENCHMARKS ? 'Benchmarks' : 'Funds';
  const METRIC_COLUMNS_WIDTH = {
    SM: 56,
    MD: 80,
    LG: 88,
    XL: 104,
    XXL: 125,
  };

  const renderNameCell = (cell: Cell<T, any>) => {
    const name = formatLocalizedString(cell.getValue());
    const { type, user } = props;
    const { original } = cell.row;
    const { flags } = original as Fund;
    const shelfName = user ? getShelfNameFromShelvableInstrument(original as ShelvableInstrument, user) : undefined;

    const holdingNameCell = (
      <span
        onMouseDown={e => {
          // Remove preventDefault to allow event to bubble to underlying <button>
          // Allow pointer events to pass through to underlying button
          // Allow pointer events to pass through to underlying button
          e.stopPropagation();
        }}
        id={`fund-name-${original._id}`}
        className={classNames('ellipsis', { md: type === SearchType.BENCHMARKS })}
      >
        {name}
      </span>
    );
    return (
      <div className="name-cell">
        <Tooltip className="custom-tooltip-v2" message={name}>
          {holdingNameCell}
        </Tooltip>
        {flags && <ShelfStatus isV2 statuses={flags} tooltipClassName="custom-tooltip-v2" title={shelfName} />}
      </div>
    );
  };

  const renderTickerCell = (cell: Cell<T, any>) => {
    const ticker = cell.getValue();
    return <span className="ticker-cell">{ticker || '-'}</span>;
  };

  const renderFundTypeCell = (cell: Cell<T, any>) => {
    const fundType = formatLocalizedString(cell.getValue());
    return (
      <Tooltip className="custom-tooltip-v2" message={fundType}>
        <span className="ellipsis">{fundType}</span>
      </Tooltip>
    );
  };

  const renderClassificationCell = (cell: Cell<T, any>) => {
    const { type } = props;
    const classification = formatLocalizedString(cell.getValue());
    const classificationCell = (
      <span className={classNames('ellipsis', { sm: type === SearchType.FUNDS, md: type === SearchType.BENCHMARKS })}>{classification}</span>
    );
    return (
      <Tooltip className="custom-tooltip-v2" message={classification}>
        {classificationCell}
      </Tooltip>
    );
  };

  const renderIntegerCell = (cell: Cell<T, any>) => {
    const value = cell.getValue();
    return <IntegerValue value={value} growth />;
  };

  const renderPercentageCell = (cell: Cell<T, any>) => {
    const value = cell.getValue();
    return <IntegerValue value={value} growth isPercentage />;
  };

  const renderRoundValueCell = (decimals: number) => (cell: Cell<T, any>) => {
    const value = cell.getValue();
    const formattedValue = typeof value === 'object' ? getRiskMetricVal(value) : value;
    return <span>{formattedValue ? formatNumber(formattedValue, decimals) : '-'}</span>;
  };

  const renderAddInlineCell = (cell: Cell<T, any>) => {
    const { onAddItem } = props;
    const { original } = cell.row;
    return (
      <Tooltip className="custom-tooltip-v2" message={formatMessage(`${i18nBasePath}.AddToPortfolio`)}>
        <IconButton className="add-icon" icon="ds-add" onClick={() => onAddItem!(original)} stopClickPropagation />
      </Tooltip>
    );
  };

  const renderAddToFavoritesCell = (cell: Cell<T, any>) => {
    const { original } = cell.row;
    const { onToggleSaved } = props;
    const active = original.isSaved;
    const classes = classNames('saved-item-btn', {
      active,
      'tst-hover-action-btn': !active,
    });
    const icon = active ? 'ds-star' : 'ds-star-border';
    const tooltipMessage = formatMessage(`${i18nBasePath}.${active ? 'RemoveFrom' : 'AddTo'}Favorites`);
    return (
      <Tooltip className="custom-tooltip-v2" message={tooltipMessage}>
        <IconButton className={classes} icon={icon} ariaLabel={tooltipMessage} onClick={() => onToggleSaved!(original)} stopClickPropagation />
      </Tooltip>
    );
  };

  const renderRemoveFromFavoritesCell = (cell: Cell<T, any>) => {
    const { original } = cell.row;
    const classes = classNames('remove-saved-item-btn');
    return (
      <Tooltip className="custom-tooltip-v2" message={formatMessage(`${i18nBasePath}.RemoveFromFavorites`)}>
        <IconButton className={classes} icon="ds-delete" onClick={() => props.onToggleSaved!(original)} stopClickPropagation />
      </Tooltip>
    );
  };

  const renderIsFocusedCell = (cell: Cell<T, any>) => {
    const isFocused = cell.getValue();

    if (!isFocused) {
      return null;
    }
    const tooltipMessage = formatMessage(`${i18nBasePath}.ViewDetails`);
    return (
      <Tooltip className="custom-tooltip-v2" message={tooltipMessage}>
        <i className="icon-cheveron-right focused-arrow" aria-label={tooltipMessage} />
      </Tooltip>
    );
  };

  const getColumns = <T extends AdvancedSearchItemForDataTable>(columnIds?: SearchResultColumns[]) => {
    const { hideSaveItem, showInlineAdd, onAddItem, onToggleSaved, locale, type, showTicker, columnOverrides } = props;

    const allColumns = [
      {
        // Note : In the new light version, this is only used for the missing product flow.
        id: SearchResultColumns.INLINE_ADD,
        accessorKey: '_id',
        header: '',
        size: 36,
        enableSorting: false,
        show: !!onAddItem && !!showInlineAdd,
        cell: renderAddInlineCell,
      },
      {
        id: SearchResultColumns.TICKER,
        accessorFn: (item: T) => {
          if ('symbols' in item && Array.isArray(item.symbols)) {
            return item.symbols[0];
          }

          if ('ticker' in item) {
            return item.ticker;
          }

          return undefined;
        },
        cell: renderTickerCell,
        header: () => <span>{formatMessage('Funds.Table.Headers.Ticker')}</span>,
        size: 96,
        minSize: 80,
        enableSorting: true,
        show: !!showTicker,
      },
      {
        id: SearchResultColumns.NAME,
        accessorKey: 'names.full',
        cell: renderNameCell,
        header: () => <span>{formatMessage(`${i18nBasePath}.Table.Headers.Name`)}</span>,
      },
      {
        id: 'universe',
        accessorKey: 'universe',
        cell: renderFundTypeCell,
        header: () => <span>{formatMessage(`${i18nBasePath}.Table.Headers.FundType`)}</span>,
        size: type === SearchType.FUNDS ? 152 : undefined,
      },
      {
        id: SearchResultColumns.CLASSIFICATION,
        accessorKey: 'classification',
        cell: renderClassificationCell,
        header: () => <span>{formatMessage(`${i18nBasePath}.Table.Headers.Classification`)}</span>,
        size: type === SearchType.FUNDS ? 152 : undefined,
      },
      {
        id: SearchResultColumns.YT,
        accessorFn: (item: T) => item.performance?.percentGrowth?.YT,
        cell: renderPercentageCell,
        header: () => <span>{formatMessage(`${i18nBasePath}.Table.Headers.YT`)}</span>,
        size: METRIC_COLUMNS_WIDTH.XL,
        meta: {
          customClass: 'key-metrics',
        },
      },
      {
        id: SearchResultColumns.ONE_YEAR,
        accessorFn: (item: T) => item.performance?.percentGrowth?.['1Y'],
        cell: renderPercentageCell,
        header: () => <span>{formatMessage(`${i18nBasePath}.Table.Headers.1Y`)}</span>,
        size: METRIC_COLUMNS_WIDTH.MD,
        meta: {
          customClass: 'key-metrics',
        },
      },
      {
        id: SearchResultColumns.THREE_YEARS,
        accessorFn: (item: T) => item.performance?.percentGrowth?.['3Y'],
        cell: renderPercentageCell,
        header: () => <span>{formatMessage(`${i18nBasePath}.Table.Headers.3Y`)}</span>,
        size: METRIC_COLUMNS_WIDTH.MD,
        meta: {
          customClass: 'key-metrics',
        },
      },
      {
        id: SearchResultColumns.FIVE_YEARS,
        accessorFn: (item: T) => item.performance?.percentGrowth?.['5Y'],
        cell: renderPercentageCell,
        header: () => <span>{formatMessage(`${i18nBasePath}.Table.Headers.5Y`)}</span>,
        size: METRIC_COLUMNS_WIDTH.MD,
        meta: {
          customClass: 'key-metrics',
        },
      },
      {
        id: SearchResultColumns.TEN_YEARS,
        accessorFn: (item: T) => item.performance?.percentGrowth?.['10Y'],
        cell: renderPercentageCell,
        header: () => <span>{formatMessage(`${i18nBasePath}.Table.Headers.10Y`)}</span>,
        size: METRIC_COLUMNS_WIDTH.MD,
        meta: {
          customClass: 'key-metrics',
        },
      },
      {
        id: SearchResultColumns.INC,
        accessorFn: (item: T) => item.performance?.percentGrowth?.INC,
        cell: renderPercentageCell,
        header: () => <span>{formatMessage(`${i18nBasePath}.Table.Headers.INC`)}</span>,
        size: METRIC_COLUMNS_WIDTH.LG,
        meta: {
          customClass: 'key-metrics',
        },
      },
      {
        id: SearchResultColumns.MER,
        accessorFn: (fund: Fund) => fund.mer,
        cell: renderRoundValueCell(2),
        header: () => <span>{formatMessage(`${i18nBasePath}.Table.Headers.MER`)}</span>,
        size: locale === LOCALE.EN_US ? METRIC_COLUMNS_WIDTH.XXL : METRIC_COLUMNS_WIDTH.SM,
        meta: {
          customClass: 'key-metrics',
        },
      },
      {
        id: SearchResultColumns.BETA,
        accessorFn: (fund: Fund) => fund.performance?.beta,
        cell: renderRoundValueCell(2),
        header: () => <span>{formatMessage(`${i18nBasePath}.Table.Headers.Beta`)}</span>,
        size: METRIC_COLUMNS_WIDTH.SM,
        meta: {
          customClass: 'key-metrics',
        },
      },
      {
        id: SearchResultColumns.STD_DEV,
        accessorFn: (item: T) => item.performance?.standardDeviation,
        cell: renderRoundValueCell(2),
        header: () => <span>{formatMessage(`${i18nBasePath}.Table.Headers.StdDev`)}</span>,
        size: METRIC_COLUMNS_WIDTH.MD,
        meta: {
          customClass: 'key-metrics',
        },
      },
      {
        id: SearchResultColumns.IS_SAVED,
        accessorFn: (item: T) => item.isSaved,
        header: () => <i className="icon-ds-star-border" />,
        size: 64,
        show: !!onToggleSaved && !hideSaveItem && !showInlineAdd,
        enableSorting: false,
        cell: renderAddToFavoritesCell,
        meta: {
          customClass: 'save-action',
        },
      },
      {
        id: SearchResultColumns.REMOVE_FROM_FAVORITES,
        accessorKey: 'isSaved',
        enableSorting: false,
        header: '',
        size: 64,
        show: props.onToggleSaved,
        cell: renderRemoveFromFavoritesCell,
      },
      {
        id: SearchResultColumns.IS_FOCUSED,
        accessorKey: 'isFocused',
        header: '',
        size: 20,
        enableSorting: false,
        cell: renderIsFocusedCell,
      },
    ];

    // Apply column overrides
    const columnsWithOverrides = allColumns.map(column => {
      const overrides = columnOverrides?.[column.id as SearchResultColumns];
      const sizeOverride = overrides?.size;

      return {
        ...column,
        ...(sizeOverride !== undefined && { size: sizeOverride }),
        meta: {
          ...(column.meta || {}),
          isRightAligned: overrides?.isRightAligned ?? (column.meta as any)?.isRightAligned ?? false,
        },
      };
    });

    // If columnIds is provided, return columns in the order specified
    if (columnIds) {
      return columnIds.map(id => columnsWithOverrides.find(col => col.id === id)).filter((col): col is NonNullable<typeof col> => !!col && col.show !== false);
    }

    // Otherwise, filter by show property only
    return columnsWithOverrides.filter(c => c.show !== false);
  };

  const getColumnsVisibilityState = () => {
    const hasFocusedFund = !!props.itemState?.focusedFund;

    const state = {
      [SearchResultColumns.UNIVERSE]: !hasFocusedFund,
      [SearchResultColumns.CLASSIFICATION]: !hasFocusedFund,
      [SearchResultColumns.YT]: !hasFocusedFund,
      [SearchResultColumns.ONE_YEAR]: !hasFocusedFund,
      [SearchResultColumns.FIVE_YEARS]: !hasFocusedFund,
      [SearchResultColumns.TEN_YEARS]: !hasFocusedFund,
      [SearchResultColumns.STD_DEV]: !hasFocusedFund,

      [SearchResultColumns.IS_FOCUSED]: hasFocusedFund,
    };

    return state;
  };

  return {
    columns: getColumns(props.allowedColumns),
    columnVisibilityState: getColumnsVisibilityState(),
  };
};
